'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Value {
    id: number;
    icon: string;
    title: string;
    description: string;
    color: string;
}

const values: Value[] = [
    {
        id: 1,
        icon: 'AcademicCapIcon',
        title: 'Excellence in Education',
        description: 'We refuse to settle for ordinary. Every aspect of our platform is crafted to deliver premium learning experiences that exceed expectations and drive real transformation.',
        color: 'from-primary to-accent'
    },
    {
        id: 2,
        icon: 'LightBulbIcon',
        title: 'Innovation & Adaptability',
        description: 'The future of education is constantly evolving. We stay ahead by embracing emerging technologies and adapting our approach to meet the changing needs of learners worldwide.',
        color: 'from-accent to-success'
    },
    {
        id: 3,
        icon: 'HeartIcon',
        title: 'Empathy & Support',
        description: 'Behind every login is a human with dreams, challenges, and potential. We design with empathy, ensuring every learner feels supported, understood, and empowered.',
        color: 'from-success to-primary'
    }
];

export default function ValuesSection() {
    const [isHydrated, setIsHydrated] = useState(false);
    const [visibleCards, setVisibleCards] = useState<number[]>([]);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    useEffect(() => {
        if (!isHydrated) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const cardId = parseInt(entry.target.getAttribute('data-card-id') || '0');
                        setVisibleCards((prev) => [...new Set([...prev, cardId])]);
                    }
                });
            },
            { threshold: 0.2 }
        );

        const cards = sectionRef.current?.querySelectorAll('[data-card-id]');
        cards?.forEach((card) => observer.observe(card));

        return () => observer.disconnect();
    }, [isHydrated]);

    if (!isHydrated) {
        return (
            <section className="py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="h-64 bg-surface rounded-xl animate-pulse" />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section ref={sectionRef} className="py-20 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl font-bold text-text-primary mb-6">
                        Our Core <span className="gradient-text">Values</span>
                    </h2>
                    <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                        The principles that guide every decision, every feature, and every interaction on our platform
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {values.map((value, index) => (
                        <div
                            key={value.id}
                            data-card-id={value.id}
                            className={`glass glass-border rounded-xl p-8 hover-lift transition-smooth ${visibleCards.includes(value.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                                }`}
                            style={{
                                transition: 'all 0.6s ease-out',
                                transitionDelay: `${index * 100}ms`
                            }}
                        >
                            <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-lg flex items-center justify-center mb-6 shadow-glow`}>
                                <Icon name={value.icon as any} size={32} variant="solid" className="text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-text-primary mb-4">{value.title}</h3>
                            <p className="text-text-secondary leading-relaxed">{value.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}